import { useEffect, useState, type RefObject } from 'react';

/**
 * Shared scroll-reveal motion tokens. Slide + fade-in animations across
 * the site (sections, buttons, hero text etc.) all reach for these so
 * the "feel" stays consistent: same easing curve, duration, and slide
 * distance regardless of which axis the element comes in on.
 */
export const REVEAL_TIMING = {
  /** Cubic ease-out — Framer Motion's default `easeOut`. Decelerates
   *  smoothly into the final position. */
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Duration applied to both transform and opacity. */
  durationMs: 700,
  /** How far the element starts off its final position, in pixels. */
  offsetPx: 60,
} as const;

/**
 * "Content slide" preset — the standard reveal used for body copy, content
 * rows, and full sections across the site. Slides up from `REVEAL_TIMING.offsetPx`
 * while fading 0 → 1, with the fade starting 30% into the slide. Triggers the
 * moment any part of the element enters the viewport.
 *
 * Apply via the spread: `<RevealOnView {...CONTENT_SLIDE}>...</RevealOnView>`.
 */
export const CONTENT_SLIDE = {
  from: 'up',
  threshold: 0,
  fadeStart: 0.3,
} as const;

/**
 * "Header fade" preset — the standard opacity fade used for section headings
 * across the site (Our technology / Roadmap / Our story). Fades 0 → 1 over
 * `durationMs` with a `pauseMs` follow-up baked in so subsequent content
 * reveals can derive their delay as `durationMs + pauseMs`.
 *
 * Apply inline:
 *   style={{
 *     opacity: visible ? 1 : 0,
 *     transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
 *   }}
 */
export const HEADER_FADE = {
  durationMs: 800,
  pauseMs: 0,
  ease: 'ease-out',
} as const;

/**
 * "Button slide" preset — the standard reveal used for trailing CTAs on the
 * home sections (Find out more / View our products / Contact us). Slides in
 * from the left while fading 0 → 1, with the fade starting 30% into the slide.
 * Triggers the moment the button enters the viewport, so it animates
 * independently of its parent section's content reveal.
 *
 * Apply via the spread: `<RevealOnView {...BUTTON_SLIDE}>...</RevealOnView>`.
 */
export const BUTTON_SLIDE = {
  from: 'left',
  threshold: 0,
  fadeStart: 0.3,
} as const;

interface UseInViewOptions {
  /**
   * IntersectionObserver threshold. Default `0` so the callback fires the
   * moment any pixel of the element enters the viewport. A higher
   * threshold is unsafe for tall sections, since `viewportHeight /
   * sectionHeight` can be smaller than the threshold itself, in which
   * case the observer never fires.
   */
  threshold?: number;
  /** Observer rootMargin. Default `'0px'` so the trigger fires as soon
   *  as the element touches the viewport edge. */
  rootMargin?: string;
  /** When `true` (default), the observer disconnects after the first
   *  intersecting hit so the reveal plays once and never replays on
   *  scroll-back. */
  once?: boolean;
}

/**
 * Tracks whether `ref`'s element is in the viewport per
 * `IntersectionObserver`. Returns a stable boolean — false until the
 * first intersection, then true for the rest of the page lifecycle (with
 * `once: true`, the default).
 */
export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { threshold = 0, rootMargin = '0px', once = true }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin, once]);

  return inView;
}
